import React, { useState, useEffect, useMemo } from 'react';
import OfficeService from './officeService';
import OfficeHoursService from './officeHoursService';
import imageService from '../images/imageService';
import './OfficeDisplay.css';

const OfficeDisplay = () => {
  const userRole = localStorage.getItem('role');

  const normalizedRole = String(userRole || '').toUpperCase();
  const canSeeBillingOffice = ['ADMIN', 'MANAGER'].includes(normalizedRole);
  const BILLING_OFFICE_ID = 6;

  const [offices, setOffices] = useState([]);
  const [selectedOffice, setSelectedOffice] = useState(null);
  const [officeImages, setOfficeImages] = useState([]);
  const [editedOffice, setEditedOffice] = useState({});
  const [uploading, setUploading] = useState(false);
  const [lightboxImageIndex, setLightboxImageIndex] = useState(null);
  const [activeTab, setActiveTab] = useState('details');

  // Hours state
  const [hoursByOffice, setHoursByOffice] = useState({});
  const [hasAnyHours, setHasAnyHours] = useState({});

  const officeService = useMemo(() => new OfficeService(), []);
  const hoursService = useMemo(() => new OfficeHoursService(), []);
  const imageDir = 'office';

  const visibleOffices = useMemo(() => {
    return (offices || []).filter((office) => {
      const officeId = Number(office.officeId ?? office.id);
      return officeId !== BILLING_OFFICE_ID || canSeeBillingOffice;
    });
  }, [offices, canSeeBillingOffice]);

  // Social icon images
  const facebookIconUrl = imageService.getBestImageUrl(
    'header',
    'facebooklogo.png',
    'thumb'
  );

  const instagramIconUrl = imageService.getBestImageUrl(
    'header',
    'instagramlogo.png',
    'thumb'
  );

  useEffect(() => {
    (async () => {
      try {
        const data = await officeService.getAllOffices();
        setOffices(data || []);
      } catch (err) {
        console.error('Failed to fetch offices:', err);
      }
    })();
  }, [officeService]);

  // Next 7 dates: today + 6
  const next7 = useMemo(() => {
    const out = [];

    for (let i = 0; i < 7; i++) {
      const d = new Date();
      d.setDate(d.getDate() + i);

      out.push({
        iso: d.toISOString().slice(0, 10),
        labelDay: d.toLocaleDateString(undefined, { weekday: 'short' }),
        labelMD: d.toLocaleDateString(undefined, {
          month: 'short',
          day: 'numeric'
        })
      });
    }

    return out;
  }, []);

  // Normalize any date to yyyy-mm-dd
  const normDate = (dateLike) => {
    if (!dateLike) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateLike)) return dateLike;

    const fromT = String(dateLike).split('T')[0];
    if (/^\d{4}-\d{2}-\d{2}$/.test(fromT)) return fromT;

    const d = new Date(dateLike);
    return isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10);
  };

  // HH:mm[:ss] -> h:mm am/pm
  const to12h = (t) => {
    if (!t) return '';

    const hhmm = t.length >= 5 ? t.slice(0, 5) : t;
    const [hStr, mStr = '00'] = hhmm.split(':');

    let h = parseInt(hStr, 10);
    if (Number.isNaN(h)) return hhmm;

    const am = h < 12;
    h = h % 12;
    if (h === 0) h = 12;

    return `${h}:${mStr} ${am ? 'am' : 'pm'}`;
  };

  // Fetch hours once per visible office and build 7-day map
  useEffect(() => {
    if (!visibleOffices?.length) return;

    const fetchHoursForOffice = async (o) => {
      try {
        const rows = await hoursService.getOfficeHoursByOfficeId(o.officeId);
        const byDate = {};
        let any = false;

        (Array.isArray(rows) ? rows : rows ? [rows] : []).forEach((r) => {
          const d = normDate(r.date);
          if (!d) return;

          const isClosedField =
            (r.dayOfWeek && String(r.dayOfWeek).toLowerCase() === 'closed') ||
            (r.openHour === '00:00' && r.closeHour === '00:00');

          if (isClosedField) {
            byDate[d] = { status: 'Closed' };
            any = true;
          } else if (r.openHour && r.closeHour) {
            byDate[d] = {
              status: 'Open',
              openHour: r.openHour,
              closeHour: r.closeHour,
              incraments: r.incraments ?? r.increments ?? r.increment ?? null
            };
            any = true;
          } else {
            byDate[d] = { status: 'Closed' };
            any = true;
          }
        });

        const sevenDayMap = {};

        next7.forEach(({ iso }) => {
          if (byDate[iso]) {
            sevenDayMap[iso] = byDate[iso];
          } else if (any) {
            sevenDayMap[iso] = { status: 'Closed' };
          }
        });

        setHoursByOffice((prev) => ({
          ...prev,
          [o.officeId]: sevenDayMap
        }));

        setHasAnyHours((prev) => ({
          ...prev,
          [o.officeId]: any
        }));
      } catch {
        setHoursByOffice((prev) => ({
          ...prev,
          [o.officeId]: {}
        }));

        setHasAnyHours((prev) => ({
          ...prev,
          [o.officeId]: false
        }));
      }
    };

    visibleOffices.forEach(fetchHoursForOffice);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleOffices, next7, hoursService]);

  const getOfficeImageList = (office) => {
    return office?.officeImage
      ? office.officeImage
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      : [];
  };

  const openPopup = (office, tab = 'details') => {
    setSelectedOffice(office);
    setEditedOffice({ ...office });
    setOfficeImages(getOfficeImageList(office));
    setActiveTab(tab);
  };

  const closePopup = () => {
    setSelectedOffice(null);
    setOfficeImages([]);
    setLightboxImageIndex(null);
    setActiveTab('details');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let parsedValue = value;

    if (name === 'googleRating') {
      parsedValue = value === '' ? null : parseFloat(value);
    }

    setEditedOffice((prev) => ({
      ...prev,
      [name]: parsedValue
    }));
  };

  const handleUpload = async (e) => {
    const files = e.target.files;

    if (!files || files.length === 0 || !selectedOffice?.officeName) {
      alert('No file selected or office not chosen.');
      return;
    }

    setUploading(true);

    const uploaded = [];

    for (const file of files) {
      if (!file || file.size === 0) continue;

      try {
        await imageService.uploadImage(file, file.name, imageDir);
        uploaded.push(file.name);
      } catch (err) {
        console.error(`Upload failed for ${file.name}:`, err);
      }
    }

    setOfficeImages((prev) => [...prev, ...uploaded]);
    setUploading(false);

    e.target.value = '';
  };

  const deleteImage = async (filenameToRemove) => {
    const updatedList = officeImages.filter((img) => img !== filenameToRemove);
    setOfficeImages(updatedList);

    try {
      await imageService.deleteImage(imageDir, filenameToRemove);
    } catch (err) {
      console.error('Failed to delete image:', err);
    }
  };

  const saveOfficeChanges = async () => {
    const updatedOffice = {
      ...editedOffice,
      officeImage: officeImages.join(',')
    };

    try {
      await officeService.updateOffice(selectedOffice.officeId, updatedOffice);
      alert('Office info updated');

      setSelectedOffice({
        ...selectedOffice,
        ...updatedOffice
      });

      setOffices((prev) =>
        prev.map((office) =>
          office.officeId === selectedOffice.officeId
            ? {
                ...office,
                ...updatedOffice
              }
            : office
        )
      );

      setActiveTab('details');
    } catch (err) {
      alert('Failed to update office info');
      console.error(err);
    }
  };

  const handleLightboxPrev = (e) => {
    e.stopPropagation();

    setLightboxImageIndex(
      (lightboxImageIndex - 1 + officeImages.length) % officeImages.length
    );
  };

  const handleLightboxNext = (e) => {
    e.stopPropagation();

    setLightboxImageIndex((lightboxImageIndex + 1) % officeImages.length);
  };

  const handleAppointmentClick = (officeId) => {
    localStorage.setItem('office_id', officeId);
    window.location.href = '/appointments';
  };

  return (
    <div className="office-container">
      <div className="office-description">
        <h1>Our Offices</h1>

        <p>
          Explore our locations below. Tap <strong>Details</strong> to see maps,
          hours, and contact options, or open the <strong>Gallery</strong> to
          view photos from each office.
        </p>
      </div>

      {visibleOffices.map((office) => {
        const firstImg = office.officeImage?.split(',')[0]?.trim();
        const hoursForOffice = hoursByOffice[office.officeId] || {};
        const anyHours = !!hasAnyHours[office.officeId];

        const rating =
          office.googleRating !== null && office.googleRating !== undefined
            ? Number(office.googleRating)
            : null;

        let ratingStars = '';

        if (rating !== null && !Number.isNaN(rating)) {
          const rounded = Math.round(rating);
          ratingStars = '★'.repeat(rounded) + '☆'.repeat(5 - rounded);
        }

        const today = next7[0];
        const todayEntry = hoursForOffice[today.iso];

        let todayLabel = 'Not available';

        if (anyHours && todayEntry) {
          todayLabel =
            todayEntry.status === 'Closed'
              ? 'Closed'
              : `${to12h(todayEntry.openHour)} – ${to12h(todayEntry.closeHour)}`;
        } else if (anyHours && !todayEntry) {
          todayLabel = 'Closed';
        }

        return (
          <div key={office.officeId} className="office-card-row">
            <div className="office-main">
              <div className="office-image-wrapper">
                <img
                  src={imageService.getBestImageUrl(imageDir, firstImg, 'thumb')}
                  alt={`${office.officeName} preview`}
                  className="office-image-modern"
                  loading="lazy"
                  decoding="async"
                />
              </div>

              <div className="office-info">
                <h2 className="office-title">{office.officeName}</h2>

                {rating !== null && !Number.isNaN(rating) ? (
                  <div className="office-rating">
                    <span className="stars">{ratingStars}</span>
                    <span className="rating-number">
                      {' '}
                      {rating.toFixed(1)} / 5.0
                    </span>
                  </div>
                ) : (
                  <div className="office-rating no-rating">No rating set</div>
                )}

                <p className="office-address">
                  {office.address || 'No address provided'}
                </p>

                <p className="office-hours-snippet">
                  <strong>Today:</strong> {todayLabel}
                </p>

                <div className="office-social-icons">
                  {office.facebookLink && (
                    <a
                      href={office.facebookLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="office-social-link"
                    >
                      <img
                        src={facebookIconUrl}
                        alt="Facebook"
                        className="socialIcon"
                        loading="lazy"
                        decoding="async"
                      />
                    </a>
                  )}

                  {office.instagramLink && (
                    <a
                      href={office.instagramLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="office-social-link"
                    >
                      <img
                        src={instagramIconUrl}
                        alt="Instagram"
                        className="socialIcon"
                        loading="lazy"
                        decoding="async"
                      />
                    </a>
                  )}
                </div>
              </div>
            </div>

            <div className="office-actions">
              <button
                className="office-action-btn"
                onClick={() => openPopup(office, 'details')}
              >
                Details
              </button>

              <button
                className="office-action-btn"
                onClick={() => openPopup(office, 'gallery')}
              >
                Gallery
              </button>

              <button
                className="office-action-btn"
                onClick={() => openPopup(office, 'details')}
              >
                Map
              </button>

              <button
                className="office-action-btn"
                onClick={() => openPopup(office, 'details')}
              >
                Hours
              </button>

              {office.phoneNumber && (
                <a
                  className="office-action-btn ghost"
                  href={`tel:${office.phoneNumber}`}
                >
                  Call
                </a>
              )}

              {office.email && (
                <a
                  className="office-action-btn ghost"
                  href={`mailto:${office.email}`}
                >
                  Email
                </a>
              )}

              <button
                className="office-action-btn primary"
                onClick={() => handleAppointmentClick(office.officeId)}
              >
                Appointment
              </button>
            </div>
          </div>
        );
      })}

      {selectedOffice && (
        <div className="popup-overlay">
          <div className="popup-content modern">
            <span className="popup-close" onClick={closePopup}>
              &times;
            </span>

            <h2 className="popup-title">{selectedOffice.officeName}</h2>

            <div className="popup-tabs">
              <button
                className={`popup-tab ${
                  activeTab === 'details' ? 'active' : ''
                }`}
                onClick={() => setActiveTab('details')}
              >
                Details
              </button>

              <button
                className={`popup-tab ${
                  activeTab === 'gallery' ? 'active' : ''
                }`}
                onClick={() => setActiveTab('gallery')}
              >
                Gallery
              </button>

              {['ADMIN', 'MANAGER', 'DOCTOR'].includes(normalizedRole) && (
                <button
                  className={`popup-tab ${
                    activeTab === 'edit' ? 'active' : ''
                  }`}
                  onClick={() => setActiveTab('edit')}
                >
                  Edit
                </button>
              )}
            </div>

            {activeTab === 'edit' && (
              <>
                <label>
                  Office Name:
                  <input
                    name="officeName"
                    value={editedOffice.officeName || ''}
                    onChange={handleInputChange}
                  />
                </label>

                <label>
                  Address:
                  <input
                    name="address"
                    value={editedOffice.address || ''}
                    onChange={handleInputChange}
                  />
                </label>

                <label>
                  Description:
                  <textarea
                    name="description"
                    value={editedOffice.description || ''}
                    onChange={handleInputChange}
                  />
                </label>

                <label>
                  Phone Number:
                  <input
                    name="phoneNumber"
                    value={editedOffice.phoneNumber || ''}
                    onChange={handleInputChange}
                  />
                </label>

                <label>
                  Email:
                  <input
                    name="email"
                    value={editedOffice.email || ''}
                    onChange={handleInputChange}
                  />
                </label>

                <label>
                  Google Maps Embed URL:
                  <input
                    name="googleEmbedUrl"
                    value={editedOffice.googleEmbedUrl || ''}
                    onChange={handleInputChange}
                    placeholder="Paste the Google 'Embed a map' src URL here"
                  />
                </label>

                <label>
                  Google Rating stars:
                  <input
                    name="googleRating"
                    type="number"
                    step="0.1"
                    min="0"
                    max="5"
                    value={editedOffice.googleRating ?? ''}
                    onChange={handleInputChange}
                    placeholder="e.g. 4.8"
                  />
                </label>

                <label>
                  Facebook Link:
                  <input
                    name="facebookLink"
                    value={editedOffice.facebookLink || ''}
                    onChange={handleInputChange}
                    placeholder="https://facebook.com/..."
                  />
                </label>

                <label>
                  Instagram Link:
                  <input
                    name="instagramLink"
                    value={editedOffice.instagramLink || ''}
                    onChange={handleInputChange}
                    placeholder="https://instagram.com/..."
                  />
                </label>

                <div>
                  <label>Upload Image(s):</label>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleUpload}
                    disabled={uploading}
                  />
                </div>

                {officeImages.length > 0 && (
                  <div className="image-grid">
                    {officeImages.map((img, index) => (
                      <div key={index} className="grid-img-wrapper">
                        <img
                          src={imageService.getBestImageUrl(
                            imageDir,
                            img,
                            'medium'
                          )}
                          alt={`${selectedOffice.officeName} edit img`}
                          className="grid-img"
                          loading="lazy"
                          decoding="async"
                          onClick={() => setLightboxImageIndex(index)}
                        />

                        <button
                          className="delete-img-btn"
                          onClick={() => deleteImage(img)}
                          type="button"
                        >
                          🗑
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <button onClick={saveOfficeChanges} disabled={uploading}>
                  {uploading ? 'Uploading...' : 'Save Changes'}
                </button>
              </>
            )}

            {activeTab === 'details' && (
              <>
                <div className="details-grid">
                  <div>
                    <p>
                      <strong>Address:</strong>{' '}
                      {selectedOffice.address || 'Not Provided'}
                    </p>

                    <p>
                      <strong>Description:</strong>{' '}
                      {selectedOffice.description || 'Not Provided'}
                    </p>

                    <p>
                      <strong>Phone:</strong>{' '}
                      {selectedOffice.phoneNumber || 'Not Provided'}
                    </p>

                    <p>
                      <strong>Email:</strong>{' '}
                      {selectedOffice.email || 'Not Provided'}
                    </p>

                    {selectedOffice.googleRating !== null &&
                    selectedOffice.googleRating !== undefined ? (
                      <p>
                        <strong>Google Rating:</strong>{' '}
                        {Number(selectedOffice.googleRating).toFixed(1)} / 5.0
                      </p>
                    ) : null}

                    {(selectedOffice.facebookLink ||
                      selectedOffice.instagramLink) && (
                      <p>
                        <strong>Social:</strong>{' '}

                        {selectedOffice.facebookLink && (
                          <a
                            href={selectedOffice.facebookLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="office-social-link"
                          >
                            <img
                              src={facebookIconUrl}
                              alt="Facebook"
                              className="socialIcon"
                              loading="lazy"
                              decoding="async"
                            />
                          </a>
                        )}

                        {selectedOffice.instagramLink && (
                          <a
                            href={selectedOffice.instagramLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="office-social-link"
                          >
                            <img
                              src={instagramIconUrl}
                              alt="Instagram"
                              className="socialIcon"
                              loading="lazy"
                              decoding="async"
                            />
                          </a>
                        )}
                      </p>
                    )}
                  </div>

                  <div className="details-map-wrap">
                    {(() => {
                      const hasMapData =
                        selectedOffice.googleEmbedUrl || selectedOffice.address;

                      if (!hasMapData) return <p>No map available.</p>;

                      const embedSrc =
                        selectedOffice.googleEmbedUrl ||
                        (selectedOffice.address
                          ? `https://www.google.com/maps?q=${encodeURIComponent(
                              selectedOffice.address
                            )}&output=embed`
                          : '');

                      if (!embedSrc) return <p>No map available.</p>;

                      return (
                        <iframe
                          title={`Map & reviews for ${selectedOffice.officeName}`}
                          src={embedSrc}
                          loading="lazy"
                          allowFullScreen
                          referrerPolicy="no-referrer-when-downgrade"
                        />
                      );
                    })()}
                  </div>
                </div>

                <div className="hours-card popup-hours">
                  <h4>Hours next 7 days</h4>

                  {(() => {
                    const hoursForSel =
                      hoursByOffice[selectedOffice.officeId] || {};

                    const anySel = !!hasAnyHours[selectedOffice.officeId];

                    if (!anySel) {
                      return <div className="hours-na">Not available</div>;
                    }

                    return (
                      <table className="hours-table">
                        <tbody>
                          {next7.map(({ iso, labelDay, labelMD }) => {
                            const entry = hoursForSel[iso];

                            if (!entry || entry.status === 'Closed') {
                              return (
                                <tr key={iso}>
                                  <td className="hours-daycell">
                                    <div className="hd">{labelDay}</div>
                                    <div className="md">{labelMD}</div>
                                  </td>

                                  <td className="hours-timecell">Closed</td>
                                </tr>
                              );
                            }

                            return (
                              <tr key={iso}>
                                <td className="hours-daycell">
                                  <div className="hd">{labelDay}</div>
                                  <div className="md">{labelMD}</div>
                                </td>

                                <td className="hours-timecell">
                                  {to12h(entry.openHour)} –{' '}
                                  {to12h(entry.closeHour)}

                                  {normalizedRole === 'ADMIN' &&
                                  entry.incraments != null ? (
                                    <span className="hours-incr">
                                      {' '}
                                      • incr: {entry.incraments}/30min
                                    </span>
                                  ) : null}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    );
                  })()}
                </div>
              </>
            )}

            {activeTab === 'gallery' && (
              <>
                {officeImages.length > 0 ? (
                  <div className="image-grid">
                    {officeImages.map((img, index) => (
                      <div key={index} className="grid-img-wrapper">
                        <img
                          src={imageService.getBestImageUrl(
                            imageDir,
                            img,
                            'medium'
                          )}
                          alt={`${selectedOffice.officeName} img`}
                          className="grid-img"
                          loading="lazy"
                          decoding="async"
                          onClick={() => setLightboxImageIndex(index)}
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <p>No images uploaded for this office yet.</p>
                )}

                {['ADMIN', 'MANAGER', 'DOCTOR'].includes(normalizedRole) && (
                  <p className="gallery-hint">
                    To add or remove photos, use the <strong>Edit</strong> tab.
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {lightboxImageIndex !== null && (
        <div
          className="lightbox-overlay"
          onClick={() => setLightboxImageIndex(null)}
        >
          <button className="lightbox-nav left" onClick={handleLightboxPrev}>
            ‹
          </button>

          <img
            src={imageService.getBestImageUrl(
              imageDir,
              officeImages[lightboxImageIndex],
              'lightbox'
            )}
            className="lightbox-image"
            alt="Lightbox"
            decoding="async"
            onClick={(e) => e.stopPropagation()}
          />

          <button className="lightbox-nav right" onClick={handleLightboxNext}>
            ›
          </button>
        </div>
      )}
    </div>
  );
};

export default OfficeDisplay;